import React, { useContext, createContext } from 'react';
import { useQuery } from 'urql';
import gql from 'graphql-tag';
import { useAuthToken } from './hooks/use-auth-token';
import { useSessionId } from './hooks/use-session-id';
import { useUserDeliveryAndPickupDetails } from './hooks/use-user-delivery-and-pickup-details';
import { Analytics } from '../../utils/analytics-provider/analytics-provider';

const SupportCenterContext = createContext();

export const SupportCenterProvider = ({ children }) => {
  // get auth token information
  const userInfo = useAuthToken();
  // get sessionId for current session
  const sessionId = useSessionId();
  // get delivery/pickup info
  const userLocation = useUserDeliveryAndPickupDetails({ userInfo });
  // make a call to get user information if the user is logged in
  const [{ fetching, data, error }] = useQuery({
    query: gql`
      query userInfo {
        getSupportData {
          user {
            id
            isBcid
            funnelStage
            isWithin100Days
            callRouting
            profile {
              customerId
              name {
                first
                last
              }
              email
              address {
                kind
                address {
                  addressLine1
                  addressLine2
                  city
                  state
                  zipCode {
                    zip5
                    zip4
                    full
                  }
                }
              }
            }
            cart {
              id
              vehicle {
                id
              }
            }
            myCar {
              status
              fulfillment {
                fulfilledOn
              }
              isCustomerGreen
              vehicle {
                id
              }
            }
          }
        }
      }
    `
  });
  const { user } = data?.getSupportData || {};
  const { funnelStage = 'PreAccount' } = user || {};

  // set common data for analytics event
  if (Analytics) {
    Analytics.setCommonData({ funnelStage });
  }

  return (
    <SupportCenterContext.Provider
      value={{
        ...userInfo,
        ...user,
        ...userLocation,
        isLoading: fetching || userInfo.isAuthLoading,
        error,
        sessionId,
        funnelStage
      }}
    >
      {children}
    </SupportCenterContext.Provider>
  );
};

export const useSupportContext = () => {
  const context = useContext(SupportCenterContext);
  if (!context || context === undefined)
    throw new Error('useSupportContext must be used within a SupportCenterProvider');

  return context;
};
